import { type StepperSelectionEvent } from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  ViewChild,
  type OnDestroy,
  type OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStep } from '@angular/material/stepper';
import {
  AppointmentAutomationsRescheduleComponent,
  WaitlistFormGroup,
  type AppointmentDetailsComponent,
} from '@principle-theorem/ng-appointment';
import {
  DateService,
  IReasonSelectorValue,
  OrganisationService,
  TimezoneService,
} from '@principle-theorem/ng-principle-shared';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  Appointment,
  AppointmentSuggestion,
  AppointmentSuggestionEntity,
  Brand,
  DEFAULT_TIME_INCREMENT,
  Event,
  EventTimePeriod,
  GapManager,
  IAppointmentDetails,
  IMoveAppointmentData,
  IWaitListDetails,
  Practice,
  SchedulingEvent,
  SchedulingEventReason,
  Staffer,
  TreatmentStep,
  getPlanWithBookableStep,
  getWaitListDetailsFromAppointment,
  isSameTimestamp,
  staffToNamedDocs,
} from '@principle-theorem/principle-core';
import {
  ISchedulingEventConditions,
  ISchedulingEventData,
  ISchedulingEventReason,
  SchedulingEventType,
  isEventable,
  type IAppointmentSuggestion,
  type IBrand,
  type IEvent,
  type IPractice,
  type IStaffer,
  type ITreatmentCategory,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_FORMAT,
  Firestore,
  TIME_FORMAT,
  TimeBucket,
  Timezone,
  filterUndefined,
  isINamedDocument,
  snapshot,
  toEntityModel,
  toEntityModels$,
  toMomentTz,
  toTimePeriod,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import * as moment from 'moment-timezone';
import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  combineLatest,
  from,
  of,
  type Observable,
} from 'rxjs';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'pr-move-appointment',
  templateUrl: './move-appointment.component.html',
  styleUrls: ['./move-appointment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  standalone: false,
})
export class MoveAppointmentComponent implements OnInit, OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _appointmentDetailsComponent$ =
    new ReplaySubject<AppointmentDetailsComponent>(1);
  private _timezone$: Observable<Timezone>;

  practices$: Observable<WithRef<IPractice>[]>;
  practitioners$: Observable<INamedDocument<IStaffer>[]>;
  appointmentDetails$ = new ReplaySubject<IAppointmentDetails>(1);
  waitlistDetails$ = new ReplaySubject<IWaitListDetails>(1);
  automationReschedule$ =
    new ReplaySubject<AppointmentAutomationsRescheduleComponent>(1);
  waitlistForm: WaitlistFormGroup = new WaitlistFormGroup();
  appointmentOptions$: Observable<AppointmentSuggestionEntity[]>;
  @ViewChild('appointmentSelectStep', { read: MatStep, static: true })
  _appointmentSelectSet: MatStep;
  appointmentSuggestion$ = new ReplaySubject<AppointmentSuggestionEntity>(1);
  newEvent$: Observable<IEvent>;
  treatmentStep$: Observable<WithRef<ITreatmentStep>>;
  completed$: Observable<boolean>;
  selectedId$ = new BehaviorSubject<string | undefined>(undefined);
  openTime$: Observable<moment.Moment>;
  closeTime$: Observable<moment.Moment>;
  brand$: Observable<WithRef<IBrand>>;

  reasons$: Observable<WithRef<ISchedulingEventReason>[]>;
  schedulingConditions$: Observable<ISchedulingEventConditions>;
  requiresReschedulingReason$: Observable<boolean>;
  reasonControl = new TypedFormControl<IReasonSelectorValue>(
    undefined,
    Validators.required
  );

  @ViewChild('appointmentDetails', { static: true })
  set appointmentDetailsComponent(
    appointmentDetails: AppointmentDetailsComponent
  ) {
    if (appointmentDetails) {
      this._appointmentDetailsComponent$.next(appointmentDetails);
    }
  }

  @ViewChild(AppointmentAutomationsRescheduleComponent, { static: false })
  set automationReschedule(
    component: AppointmentAutomationsRescheduleComponent
  ) {
    if (component) {
      this.automationReschedule$.next(component);
    }
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IMoveAppointmentData,
    public dialogRef: MatDialogRef<MoveAppointmentComponent>,
    public dateService: DateService,
    private _snackBar: MatSnackBar,
    private _organisation: OrganisationService,
    private _timezone: TimezoneService
  ) {
    this.brand$ = this._organisation.brand$.pipe(filterUndefined());
    this.practices$ = this._organisation.practices$;

    if (isEventable(this.data.appointment)) {
      const appointmentSuggestion = toEntityModel(
        AppointmentSuggestion.init({
          event: this.data.appointment.event,
          practitioner: this.data.appointment.practitioner,
          score: 1,
        })
      );
      this.appointmentSuggestion$.next(appointmentSuggestion);
    }

    const practice$ = Firestore.doc$(this.data.gap.event.practice.ref);
    this.openTime$ = practice$.pipe(
      map((practice) => Practice.openTime(practice))
    );
    this.closeTime$ = practice$.pipe(
      map((practice) => Practice.closeTime(practice))
    );

    this.loadAppointmentOptions();
    GapManager.copyGapEventToAppointment(
      this.data.gap,
      this.data.gapCandidate.candidate,
      this.data.appointment
    );

    this.completed$ = this.appointmentSuggestion$.pipe(map((event) => !!event));

    this.practitioners$ = combineLatest([
      this.appointmentDetails$.pipe(map((details) => details.practice)),
      this.brand$,
    ]).pipe(
      switchMap(([practice, brand]) =>
        practice
          ? Staffer.practitionersByPractice$(practice)
          : Staffer.practitionersByBrand$(brand)
      ),
      staffToNamedDocs()
    );

    this._appointmentDetailsComponent$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((component) => {
        component.form.disable();
        component.form.controls.duration.enable();
      });

    this.brand$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((brand) =>
        this.waitlistDetails$.next(
          getWaitListDetailsFromAppointment(this.data.appointment, brand)
        )
      );

    this.treatmentStep$ = Appointment.treatmentStep$(this.data.appointment);
    this.newEvent$ = this.appointmentSuggestion$.pipe(
      map((suggestion) => suggestion.event)
    );

    this._timezone$ = this._timezone.resolvePracticeTimezone$(
      this.data.appointment.practice.ref
    );

    this.schedulingConditions$ = combineLatest([
      this._timezone$,
      this.appointmentSuggestion$,
      from(Firestore.getDoc(this.data.appointment.ref)),
    ]).pipe(
      map(([timezone, appointmentSuggestion, appointment]) =>
        SchedulingEvent.getSchedulingConditions(
          timezone,
          appointment.event?.from,
          appointmentSuggestion?.event.from,
          undefined,
          true
        )
      )
    );

    this.requiresReschedulingReason$ = this.schedulingConditions$.pipe(
      map((conditions) => conditions.eventType !== SchedulingEventType.Schedule)
    );

    const reasons$ = this.brand$.pipe(
      filterUndefined(),
      switchMap((brand) => Brand.cancellationReasons$(brand))
    );
    this.reasons$ = combineLatest([reasons$, this.schedulingConditions$]).pipe(
      map(([reasons, conditions]) =>
        SchedulingEventReason.filterByEventType(reasons, conditions.eventType)
      )
    );
  }

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async load(): Promise<void> {
    const treatment = await getPlanWithBookableStep(this.data.appointment);
    const practice = await snapshot(
      Appointment.practice$(this.data.appointment)
    );
    this.appointmentDetails$.next({
      practitioner: this.data.appointment.practitioner,
      duration: this.getDuration(),
      practice,
      treatment,
    });
  }

  waitlistChange(change: IWaitListDetails): void {
    this.waitlistForm.patchValue(change);
  }

  getDuration(): number {
    const gapDuration = Event.duration(this.data.gap.event);
    const appointmentDuration = Appointment.duration(this.data.appointment);
    return gapDuration > appointmentDuration
      ? appointmentDuration
      : gapDuration;
  }

  async submit(): Promise<void> {
    const appointmentSuggestion = await snapshot(this.appointmentSuggestion$);
    if (!appointmentSuggestion) {
      return;
    }

    const appointmentDetails = await snapshot(this.appointmentDetails$);
    const staffer = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );

    const automationRescheduleComponent = await snapshot(
      this.automationReschedule$
    );
    await automationRescheduleComponent.update();

    const reasonFormData = this.reasonControl.getRawValue() as
      | IReasonSelectorValue
      | undefined;
    const practice = await snapshot(
      this._organisation.practice$.pipe(filterUndefined())
    );
    const schedulingEventData: ISchedulingEventData = {
      scheduledByPractice: practice.ref,
      reason: reasonFormData?.reason,
      reasonSetManually: reasonFormData?.reasonSetManually ?? false,
      schedulingConditions: await snapshot(this.schedulingConditions$),
    };

    await GapManager.approveCandidate(
      staffer,
      this.data,
      {
        ...appointmentDetails,
        waitListItem: { ...this.waitlistForm.getRawValue() },
        event: appointmentSuggestion.event,
      },
      schedulingEventData
    );

    const timezone = await snapshot(
      this._timezone.resolvePracticeTimezone$(
        this.data.appointment.practice.ref
      )
    );

    this._snackBar.open(
      `Appointment rescheduled to ` +
        `${toMomentTz(appointmentSuggestion.event.from, timezone).format(
          DATE_FORMAT
        )} @ ` +
        `${toMomentTz(appointmentSuggestion.event.from, timezone).format(
          TIME_FORMAT
        )}`
    );

    this.dialogRef.close();
  }

  loadAppointmentOptions(): void {
    const duration: moment.Duration = moment.duration(
      this.getDuration(),
      'minutes'
    );

    this.appointmentOptions$ = this.brand$.pipe(
      switchMap((brand) =>
        !isINamedDocument(this.data.gap.event.practice)
          ? of([])
          : allEventTimes$(
              DEFAULT_TIME_INCREMENT,
              this.data.gap.event,
              duration,
              this.data.gap.event.practice,
              this.data.appointment.practitioner,
              brand,
              TreatmentStep.defaultDisplayRef(
                this.data.appointment.treatmentPlan.treatmentStep.display
              )
            )
      ),
      toEntityModels$(),
      tap((options) => this.setSuggestionFromOptions(options))
    );
  }

  setSuggestionFromOptions(options: AppointmentSuggestionEntity[]): void {
    if (options.length === 1) {
      this.appointmentSuggestion$.next(options[0]);
      return;
    }

    const foundSuggestion = options.find((suggestion) => {
      if (
        isSameTimestamp(
          suggestion.event.from,
          this.data.gapCandidate.candidate.offerTimeFrom
        ) &&
        isSameTimestamp(
          suggestion.event.to,
          this.data.gapCandidate.candidate.offerTimeTo
        )
      ) {
        return true;
      }
    });

    if (!foundSuggestion) {
      return;
    }
    this.appointmentSuggestion$.next(foundSuggestion);
  }

  handleStepChange(event: StepperSelectionEvent): void {
    if (event.selectedStep === this._appointmentSelectSet) {
      void this.loadAppointmentOptions();
    }
  }
}

export function allEventTimes$(
  timeIncrement: moment.Duration,
  event: IEvent,
  duration: moment.Duration,
  practice: INamedDocument<IPractice>,
  staffer: INamedDocument<IStaffer>,
  brand: WithRef<IBrand>,
  treatmentCategoryRef?: DocumentReference<ITreatmentCategory>
): Observable<IAppointmentSuggestion[]> {
  const options = TimeBucket.fromEvents([event])
    .exhaustTimeOptions({
      timeIncrement,
      duration,
    })
    .get()
    .map((period) =>
      EventTimePeriod.init({
        ...period,
        practice,
        staffer,
      })
    );

  return Brand.findCalendarEventsIntersectingRange$(
    brand,
    toTimePeriod(event.from, event.to)
  ).pipe(
    map((calendarEvents) =>
      AppointmentSuggestion.convertOptionsToSuggestions(
        staffer,
        options,
        [],
        event,
        calendarEvents,
        {
          distance: false,
          duration: false,
        },
        compact([treatmentCategoryRef])
      )
    )
  );
}
